import { Card, CardContent } from "../../ui/card"
import { SummaryHeader } from "../../ui/SummaryHeader"
import { useChartColors } from "../../../hooks/useChartColors"
import SalesLoader from "../../../components/loaders/SalesLoader"
import { useMemo, useState } from "react"
import { endOfMonth, format, startOfMonth } from "date-fns";

import { DataTable } from "../../ui/data-table/data-table"
import { Button } from "../../ui/button";
import { FileWarning, Filter, CalendarClock } from "lucide-react";
import { useWholesaleSales } from "../hooks/useSalesWholesale"
import { WholesalePerformanceFilter } from "./wholesale-performanceFilter"
import { useWholesaleDataCheck } from "../hooks/useWholesaleDataCheck";
import { formatCurrency } from "../../../utils/formatCurrency"
import { formatNumber } from "../../../utils/formatNumber"
import { MonthYearPicker } from "../../ui/month-Year-Picker"


interface CompletePerformanceProps {
  selectedMonth: string;
}


interface CompletePerformanceMetrics {
  style: string,
  product_name: string,
  color: string,
  color_description: string,
  class: string,
  gender: string,
  category: string,
  merch_collection_description: string,
  units_sold: number,
  revenue: number,
  average_order: number
}

export function WholesaleCompletePerformance({ selectedMonth }: CompletePerformanceProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [numericRanges, setNumericRanges] = useState<Record<string, [number?, number?]>>({});

  const now = new Date();
  const initialDate = selectedMonth ? (() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 1); // month - 1 because Date months are 0-indexed but yyyy-MM string is 1-indexed
  })() : new Date(now.getFullYear(), now.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
  
  const jumpToCurrentMonth = () => {
    const current = new Date();
    setSelectedDate(new Date(current.getFullYear(), current.getMonth()));
  };

  const { startDate, endDate } = useMemo(() => {
    const monthDate = selectedDate ?? new Date(now.getFullYear(), now.getMonth());
    return {
      startDate: format(startOfMonth(monthDate), "yyyy-MM-dd"),
      endDate: format(endOfMonth(monthDate), "yyyy-MM-dd"),
    };
  }, [selectedDate, now]);

  const { data, isLoading } = useWholesaleSales(startDate, endDate);

  const { hasAnyData: hasData } = useWholesaleDataCheck(data?.wholesale_complete_performance || []);
  const hasDataForSelectedMonth = hasData;


  const wholesaleData: CompletePerformanceMetrics[] = useMemo(
    () =>
      (data?.wholesale_complete_performance || []).map((cat) => ({
        style: cat.style,
        color: cat.color,
        color_description: cat.color_description,
        class: cat.class,
        gender: cat.gender,
        category: cat.category,
        merch_collection_description: cat.merch_collection_description,
        product_name: cat.product_name,
        units_sold: cat.units_sold,
        average_order: cat.average_order,
        revenue: cat.revenue,
      })),
    [data]
  );
 

  const filterOptions = useMemo(() => {
    const opts: Record<string, Set<string>> = {
      style: new Set(),
      color: new Set(),
      color_description: new Set(),
      class: new Set(),
      gender: new Set(),
      category: new Set(),
      merch_collection_description: new Set(),
      product_name: new Set(),
    };
    wholesaleData.forEach((row) => {
      Object.keys(opts).forEach((field) => {
        if (row[field as keyof CompletePerformanceMetrics]) {
          opts[field].add(String(row[field as keyof CompletePerformanceMetrics]));
        }
      });
    });
    return Object.fromEntries(
      Object.entries(opts).map(([k, v]) => [k, Array.from(v).sort()])
    );
  }, [wholesaleData]);

  const filteredData = useMemo(() => {
    const hasCategorical = filters && Object.keys(filters).some((f) => (filters[f] || []).length > 0);
    const hasNumeric = Object.keys(numericRanges).some((f) => {
      const [min, max] = numericRanges[f] || [];
      return min !== undefined || max !== undefined;
    });
    if (!hasCategorical && !hasNumeric) return wholesaleData;
    return wholesaleData.filter((row) => {
      const catOk = Object.entries(filters).every(([field, vals]) => {
        if (!vals || vals.length === 0) return true;
        return vals.includes(String(row[field as keyof CompletePerformanceMetrics]));
      });
      const numOk = ["average_order", "units_sold", "revenue"].every((field) => {
        const [min, max] = numericRanges[field] || [];
        const value = row[field as keyof CompletePerformanceMetrics] as number;
        if (min !== undefined && value < min) return false;
        if (max !== undefined && value > max) return false;
        return true;
      });
      return catOk && numOk;
    });
  }, [wholesaleData, filters, numericRanges]);

  
   const { hasAnyData } = useWholesaleDataCheck(wholesaleData);

  const columns = [
    {
      accessorKey: "style",
      header: "SKU",
      cell: (info: any) => info.getValue(),
    },
    {
      accessorKey: "product_name",
      header: "Product Name",
      cell: (info: any) => info.getValue(),
    },
    {
      accessorKey: "merch_collection_description",
      header: "Collection Description",
      cell: (info: any) => info.getValue(),
    },
    {
      accessorKey: "class",
      header: "Class",
      cell: (info: any) => info.getValue(),
    },
    {
      accessorKey: "color",
      header: "Color",
      cell: (info: any) => info.getValue(),
    },
    {
      accessorKey: "color_description",
      header: "Color Description",
      cell: (info: any) => info.getValue(),
    },
    {
      accessorKey: "gender",
      header: "Gender",
      cell: (info: any) => info.getValue(),
    },
    {
      accessorKey: "category",
      header: "Category",
      cell: (info: any) => info.getValue(),
    },
    {
      accessorKey: "average_order",
      header: "Average Price",
      cell: (info: any) => formatCurrency(info.getValue()),
    },
    {
      accessorKey: "units_sold",
      header: "Units Sold",
      cell: (info: any) => formatNumber(info.getValue()),
    },
    {
      accessorKey: "revenue",
      header: "Revenue",
      cell: (info: any) => formatCurrency(info.getValue()),
    },
  ];

  const colors = useChartColors("wholesale");
  if (!colors) return null;

  return (
    <>
      <SummaryHeader title="Performance Table" />
      <Card>
        <div className="flex items-center justify-between m-4">
          <Button variant="outline" size="sm" onClick={() => setFilterOpen(true)}>
            <Filter className="w-4 h-4 mr-1" /> Advance Filter
          </Button>
           <div className="flex flex-row">
          {!hasDataForSelectedMonth && (
                            <div className="text-sm text-red-600 bg-white/70 dark:bg-black/70 rounded-lg shadow-xl-soft px-1 py-2 mb-4 flex flex-row items-center">
                                <FileWarning /> Missing file uploads for the selected month.
                            </div>
                        )}
          <div className="flex items-center gap-2 pr-3 ml-2">
            <Button variant="outline" size="sm" onClick={jumpToCurrentMonth}>
              <CalendarClock className="h-4 w-4 mr-1" />
              Current Month
            </Button>
            <MonthYearPicker selected={selectedDate} onChange={setSelectedDate} />
          </div>
          </div>
        </div>
        <WholesalePerformanceFilter
          isOpen={filterOpen}
          onClose={() => setFilterOpen(false)}
          filters={filters}
          setFilters={setFilters}
          options={filterOptions}
          numericRanges={numericRanges}
          setNumericRanges={setNumericRanges}
        />
        <CardContent>
          {isLoading ? (
            <SalesLoader variant="performance-channel" />
          ) : !hasAnyData ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No Data Available
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              searchColumn="sku"
              searchPlaceholder="Search by SKU..."
              showRowsPerPage={false}
              initialPageSize={5}
              showTotal={true}
              totalColumns={["revenue"]}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}

